import { Component, Output, EventEmitter, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ReportConfigService } from '../../../modules/report/services/report-config.service';
import { LoggingService } from '../../../core/services/logging/logging.service';

declare const bootstrap: any;

export interface ReportType {
  id: string;
  name: string;
  route: string;
  icon: string;
  color: string;
}

/**
 * Componente para seleção de tipos de relatórios
 * Busca categorias disponíveis da API dinamicamente
 */
@Component({
  selector: 'app-report-type-selector',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './report-type-selector.component.html',
  styleUrls: ['./report-type-selector.component.css'],
})
export class ReportTypeSelectorComponent implements OnInit {
  @Output() typeSelected = new EventEmitter<string>();

  readonly modalId = 'reportTypeSelectorModal';
  selectedTypeId: string = '';
  reportTypes: ReportType[] = [];
  isLoading = false;

  // Mapeamento de categorias para ícones e cores
  private readonly categoryConfig: Record<string, { icon: string; color: string }> = {
    'pluviômetros': { icon: 'fa-cloud-rain', color: '#0d6efd' },
    'balanço hídrico': { icon: 'fa-droplet', color: '#198754' },
    'estações': { icon: 'fa-tower-broadcast', color: '#fd7e14' },
    'colaboradores': { icon: 'fa-users', color: '#6f42c1' },
    'default': { icon: 'fa-file-alt', color: '#6c757d' },
  };

  constructor(
    private readonly router: Router,
    private readonly reportConfigService: ReportConfigService,
    private readonly logger: LoggingService
  ) {}

  ngOnInit(): void {
    this.loadReportTypes();
  }

  /**
   * Carrega tipos de relatórios disponíveis da API
   */
  loadReportTypes(): void {
    this.isLoading = true;
    this.reportConfigService.getReportConfigs().subscribe({
      next: (configs) => {
        // Extrai categorias únicas
        const categories = new Set<string>();
        configs.forEach(config => {
          if (config.category) {
            categories.add(config.category);
          }
        });

        // Converte categorias em tipos de relatório
        this.reportTypes = Array.from(categories).map(category => {
          const categoryKey = category.toLowerCase();
          const config = this.categoryConfig[categoryKey] || this.categoryConfig['default'];
          
          return {
            id: this.categoryToId(category),
            name: category,
            route: `/relatorios/${this.categoryToId(category)}`,
            icon: config.icon,
            color: config.color,
          };
        });

        this.isLoading = false;
        this.logger.debug('Tipos de relatório carregados', this.reportTypes);
      },
      error: (error) => {
        this.logger.error('Erro ao carregar tipos de relatório', error);
        this.isLoading = false;
        // Fallback para tipos padrão
        this.reportTypes = this.getFallbackTypes();
      },
    });
  }

  /**
   * Converte nome de categoria para ID (URL-friendly)
   */
  private categoryToId(category: string): string {
    return category
      .toLowerCase()
      .normalize('NFD')
      .replaceAll(/[\u0300-\u036f]/g, '') // Remove acentos
      .replaceAll(/\s+/g, '-'); // Substitui espaços por hífens
  }

  /**
   * Retorna tipos padrão em caso de erro na API
   */
  private getFallbackTypes(): ReportType[] {
    return [
      {
        id: 'pluviometros',
        name: 'Pluviômetros',
        route: '/relatorios/pluviometros',
        icon: 'fa-cloud-rain',
        color: '#0d6efd',
      },
      {
        id: 'balanco-hidrico',
        name: 'Balanço Hídrico',
        route: '/relatorios/balanco-hidrico',
        icon: 'fa-droplet',
        color: '#198754',
      },
      {
        id: 'estacoes',
        name: 'Estações',
        route: '/relatorios/estacoes',
        icon: 'fa-tower-broadcast',
        color: '#fd7e14',
      },
      {
        id: 'colaboradores',
        name: 'Colaboradores',
        route: '/relatorios/colaboradores',
        icon: 'fa-users',
        color: '#6f42c1',
      },
    ];
  }

  openModal(): void {
    this.selectedTypeId = '';
    const modalElement = document.getElementById(this.modalId);
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement, {
        backdrop: 'static',
        keyboard: true,
      });
      modal.show();
    }
  }

  closeModal(): void {
    const modalElement = document.getElementById(this.modalId);
    if (modalElement) {
      const modalInstance = bootstrap.Modal.getInstance(modalElement);
      modalInstance?.hide();
    }
  }

  onTypeChange(): void {
    if (this.selectedTypeId) {
      const selectedType = this.reportTypes.find(t => t.id === this.selectedTypeId);
      if (selectedType) {
        this.typeSelected.emit(selectedType.id);
        this.closeModal();
        this.router.navigate([selectedType.route]);
      }
    }
  }
}
